import { useMemo, useState } from 'react';

import { Text } from '@/design-system';
import FileIcon from '@/icons/FileIcon.svg?react';
import { cn } from '@/utils/tailwind';

import { RunSchema } from '../../../types/schema';

export interface PageDocument {
  page_content?: string;
  metadata?: Record<string, unknown>;
}

function RunPageDocument(props: {
  document: PageDocument;
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);

  const metadata = useMemo(() => {
    return Object.keys(props.document.metadata ?? {})
      .sort((a, b) => {
        const aValue = JSON.stringify(props.document.metadata?.[a]);
        const bValue = JSON.stringify(props.document.metadata?.[b]);

        const aLines = aValue.split('\n');
        const bLines = bValue.split('\n');

        if (aLines.length !== bLines.length) {
          return aLines.length - bLines.length;
        }

        return aValue.length - bValue.length;
      })
      .map((key) => {
        const value = props.document.metadata?.[key];
        return {
          key,
          value:
            typeof value === 'string' || typeof value === 'number'
              ? `${value}`
              : JSON.stringify(value),
        };
      });
  }, [props.document.metadata]);

  const filteredKeys = metadata.filter((x) => x.value.length <= 42).slice(0, 2);

  const remainingKeys = metadata.length - filteredKeys.length;

  if (!open) {
    return (
      <button
        type="button"
        className={cn(
          'flex min-w-0 max-w-full gap-4 overflow-hidden px-2 transition-colors hover:bg-secondary active:bg-secondary-hover',
          props.triggerClassName
        )}
        onClick={() => setOpen(true)}
      >
        <FileIcon className="mt-1 h-4 w-4 flex-shrink-0" />

        <Text
          variant="body"
          className="min-w-0 flex-grow overflow-hidden text-ellipsis whitespace-nowrap text-left"
        >
          {props.document.page_content?.trim().replaceAll(/\n/g, ' ')}
        </Text>

        {metadata.length > 0 && (
          <span className="flex items-center gap-1 whitespace-nowrap">
            {filteredKeys.map(({ value }, idx) => (
              <span
                className="whitespace-nowrap rounded-md bg-secondary p-1 px-1.5 text-xs"
                key={idx}
              >
                {value}
              </span>
            ))}
            {remainingKeys > 0 && (
              <span className="whitespace-nowrap rounded-md bg-secondary p-1 px-1.5 text-xs">
                +{remainingKeys}
              </span>
            )}
          </span>
        )}
      </button>
    );
  }

  return (
    <div>
      <button
        type="button"
        className={cn(
          'w-full px-2 text-left transition-colors hover:bg-secondary active:bg-secondary-hover',
          props.triggerClassName
        )}
        onClick={() => setOpen(false)}
      >
        <FileIcon className="mt-1 h-4 w-4 flex-shrink-0" />
      </button>

      <span className="flex flex-col gap-4 px-2">
        <span className="whitespace-pre-wrap">
          {props.document.page_content}
        </span>

        <span className="flex flex-col flex-wrap items-start gap-2 pb-2">
          {metadata.map(({ key, value }, idx) => {
            return (
              <span
                className="rounded-md bg-secondary p-1 px-1.5 text-xs"
                key={idx}
              >
                <span className="mr-1.5 font-mono font-bold">{key}</span>
                <span className="whitespace-pre-wrap">{value}</span>
              </span>
            );
          })}
        </span>
      </span>
    </div>
  );
}

export function asPageDocumentList(
  output: RunSchema['outputs']
): PageDocument[] | undefined {
  if (!Array.isArray(output)) return;
  if (output.length === 0) return;
  const maybeDocs = output.map((doc) => {
    if (typeof doc !== 'object' || doc == null) return false;
    if (
      Array.isArray(doc.id) &&
      doc.id.join('.') === 'langchain.schema.document.Document'
    )
      return {
        page_content: doc.kwargs.page_content,
        metadata: doc.kwargs.metadata,
      };
    if (!('type' in doc) || typeof doc.type !== 'string') return false;
    if (
      (!('page_content' in doc) || typeof doc.page_content !== 'string') &&
      (!('pageContent' in doc) || typeof doc.pageContent !== 'string')
    ) {
      return false;
    }

    return {
      page_content: doc.page_content || doc.pageContent,
      metadata: doc.metadata,
    };
  });
  if (maybeDocs.some((x) => x === false)) return;
  return maybeDocs as PageDocument[];
}

export function RunPageDocumentList(props: {
  objectKey?: string;
  documents: PageDocument[];
}) {
  return (
    <div className="flex flex-col items-stretch gap-4 break-all rounded-lg border border-secondary p-4 px-2">
      <span className="flex items-center gap-2 px-2 text-sm font-semibold text-tertiary">
        <span className="uppercase">Documents</span>
        {props.objectKey && (
          <span className="inline rounded border border-secondary px-1 font-mono font-normal">
            {props.objectKey}
          </span>
        )}
        <span className="rounded bg-tertiary px-1 font-medium text-tertiary">
          {props.documents.length}
        </span>
      </span>

      <div className="-mb-2 -mt-3 grid divide-y divide-secondary overflow-hidden empty:hidden">
        {props.documents.map((document, idx) => (
          <RunPageDocument
            document={document}
            key={idx}
            triggerClassName="py-3"
          />
        ))}
      </div>
    </div>
  );
}
